import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardHeader,
  CardProps,
  Divider,
  Grid,
  Typography,
  styled,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import { RoleOut } from 'api-client';
import React from 'react';
import { ConfirmationDialog, Loading, useAsync } from 'react-components';
import { AppControllerContext } from '../app-contexts';
import { CreateRoleDialog, CreateRoleDialogProps } from './create-role-dialog';
// import { PermissionsCard, PermissionsCardProps } from './permissions-card';

const prefix = 'role-list-card';
const classes = {
  deleteRoleButton: `${prefix}-deleterolebutton`,
};
const StyledCard = styled((props: CardProps) => <Card {...props} />)(() => ({
  [`& .${classes.deleteRoleButton}`]: {
    float: 'right',
  },
}));

interface RoleAccordionProps {
  role: RoleOut;
  onDeleteClick?: React.MouseEventHandler;
}

function RoleAccordion({ role, onDeleteClick }: RoleAccordionProps) {
  return (
    <Accordion TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>{role.name}</Typography>
        {role.description && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, alignSelf: 'center' }}>
            {role.description}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" wrap="nowrap">
          {role.description && (
            <Grid item sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {role.description}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              className={classes.deleteRoleButton}
              onClick={onDeleteClick}
            >
              Delete Role
            </Button>
          </Grid>
          {/* Permissions management — coming soon
          <Grid item>
            <PermissionsCard
              className={classes.permissionsCard}
              getPermissions={getPermissions}
              savePermission={savePermission}
              removePermission={removePermission}
            />
          </Grid>
          */}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

export interface RoleListCardProps extends Pick<CreateRoleDialogProps, 'createRole'> {
  getRoles?: () => Promise<RoleOut[]> | RoleOut[];
  deleteRole?: (role: string) => Promise<void> | void;
  // getPermissions?: (role: string) => Promise<Permission[]> | Permission[];
  // savePermission?: (role: string, permission: Permission) => Promise<void> | void;
  // removePermission?: (role: string, permission: Permission) => Promise<void> | void;
}

export function RoleListCard({
  getRoles,
  deleteRole,
  createRole,
  // getPermissions,
  // savePermission,
  // removePermission,
}: RoleListCardProps): JSX.Element {
  const safeAsync = useAsync();
  const [roles, setRoles] = React.useState<RoleOut[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedDeleteRole, setSelectedDeleteRole] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const { showAlert } = React.useContext(AppControllerContext);

  const refresh = React.useCallback(async () => {
    if (!getRoles) return;
    setLoading(true);
    try {
      const newRoles = await safeAsync(getRoles());
      setRoles(newRoles.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      showAlert('error', `Failed to get roles: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [getRoles, showAlert, safeAsync]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const handleRoleDelete = React.useMemo(
    () =>
      roles.map((r) => () => {
        setDeleting(false);
        setSelectedDeleteRole(r.name);
      }),
    [roles],
  );

  return (
    <StyledCard variant="outlined">
      <CardHeader
        title="Roles"
        titleTypographyProps={{ variant: 'h5' }}
        avatar={<SecurityIcon />}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            aria-label="create role"
          >
            New Role
          </Button>
        }
      />
      <Divider />
      <Loading loading={loading} size="50px">
        {roles.map((r, i) => (
          <RoleAccordion key={r.name} role={r} onDeleteClick={handleRoleDelete[i]} />
        ))}
        {roles.length === 0 && <div style={{ height: 100 }} />}
      </Loading>
      {openDialog && (
        <CreateRoleDialog
          open={openDialog}
          setOpen={setOpenDialog}
          createRole={
            createRole &&
            (async (name, description) => {
              await createRole(name, description);
              refresh();
            })
          }
        />
      )}
      {selectedDeleteRole && (
        <ConfirmationDialog
          open={!!selectedDeleteRole}
          title="Confirm Delete"
          submitting={!!deleting}
          onClose={() => setSelectedDeleteRole(null)}
          onSubmit={async () => {
            try {
              setDeleting(true);
              deleteRole && (await safeAsync(deleteRole(selectedDeleteRole)));
              refresh();
            } catch (e) {
              showAlert('error', `Failed to delete role: ${(e as Error).message}`);
            } finally {
              setSelectedDeleteRole(null);
            }
          }}
        >
          <Typography>{`Are you sure you want to delete "${selectedDeleteRole}"?`}</Typography>
        </ConfirmationDialog>
      )}
    </StyledCard>
  );
}
